import { Sale, Commission, getSales, getCommissions } from '../firebase/financeServices';

export interface SaleValidationIssue {
  saleNumber: string;
  saleTotal: number;
  splitTotal: number;
  difference: number;
  date: Date;
  staffNames: string[];
}

export interface CommissionValidationIssue {
  saleNumber: string;
  saleTotal: number;
  commissionTotal: number;
  commissionRate: number;
  date: Date;
  staffNames: string[];
  type: 'high-rate' | 'missing';
}

/**
 * Validates that sales splits match their corresponding sale totals
 * @param year Year to validate
 * @param month Month to validate (0-11)
 * @param staffId Optional staff ID to filter by
 * @returns Array of validation issues
 */
export const validateSaleSplits = async (
  year: number, 
  month: number,  
  staffId?: string
): Promise<SaleValidationIssue[]> => {
  try {
    // Create date range for the selected month
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    
    // Fetch all sales for the period
    const salesData = await getSales(startDate, endDate);
    
    // Fetch all commissions for the period (without staff filter first)
    const allCommissionsData = await getCommissions(undefined, startDate, endDate);
    
    // Filter to only include sales splits
    const allSalesSplits = allCommissionsData.filter(comm => comm.type === 'sale');
    
    // Group all sales splits by sale number
    const allSplitsBySaleNumber = allSalesSplits.reduce((acc, split) => {
      if (!acc[split.saleNumber]) {
        acc[split.saleNumber] = [];
      }
      acc[split.saleNumber].push(split);
      return acc;
    }, {} as Record<string, Commission[]>);
    
    // If staffId is provided, get the filtered sales splits for display
    let filteredSaleNumbers: Set<string> | null = null;
    if (staffId) {
      const filteredCommissionsData = await getCommissions(staffId, startDate, endDate);
      const filteredSalesSplits = filteredCommissionsData.filter(comm => comm.type === 'sale');
      filteredSaleNumbers = new Set(filteredSalesSplits.map(split => split.saleNumber));
    }
    
    // Check for discrepancies
    const issues: SaleValidationIssue[] = [];
    
    salesData.forEach(sale => {
      // Skip voided sales
      if (sale.status === 'voided') return;
      
      // If filtering by staff, only check sales that involve this staff member
      if (filteredSaleNumbers && !filteredSaleNumbers.has(sale.saleNumber)) {
        return;
      }
      
      const splits = allSplitsBySaleNumber[sale.saleNumber] || [];
      
      // Calculate total amount from splits
      const splitTotal = splits.reduce((sum, split) => sum + split.amount, 0);
      
      // Get total amount from sale
      const saleTotal = sale.total;
      
      // Check if there's a discrepancy (using a small epsilon for floating point comparison)
      const difference = Math.abs(saleTotal - splitTotal);
      if (difference > 0.01) {
        issues.push({
          saleNumber: sale.saleNumber,
          saleTotal,
          splitTotal,
          difference,
          date: sale.createdAt,
          staffNames: Array.from(new Set(splits.map(split => split.staffName)))
        });
      }
    });
    
    return issues;
  } catch (error) {
    console.error('Error validating sales splits:', error);
    throw error;
  }
};

/**
 * Validates commission rates and missing commissions
 * @param year Year to validate
 * @param month Month to validate (0-11)
 * @param staffId Optional staff ID to filter by
 * @returns Array of commission validation issues
 */
export const validateCommissions = async (
  year: number,
  month: number,
  staffId?: string
): Promise<CommissionValidationIssue[]> => {
  try {
    // Create date range for the selected month
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);
    
    // Fetch all sales for the period
    const salesData = await getSales(startDate, endDate);
    
    // Fetch all commissions for the period
    const allCommissionsData = await getCommissions(undefined, startDate, endDate);
    
    // Filter to only include commission splits (type='commission' or undefined for backward compatibility)
    const commissionSplits = allCommissionsData.filter(comm => 
      comm.type === 'commission' || !comm.type
    );
    
    // Group commission splits by sale number
    const commissionsBySaleNumber = commissionSplits.reduce((acc, comm) => {
      if (!acc[comm.saleNumber]) {
        acc[comm.saleNumber] = [];
      }
      acc[comm.saleNumber].push(comm);
      return acc;
    }, {} as Record<string, Commission[]>);
    
    // If staffId is provided, get the filtered sales for display
    let filteredSaleNumbers: Set<string> | null = null;
    if (staffId) {
      const filteredCommissionsData = await getCommissions(staffId, startDate, endDate);
      filteredSaleNumbers = new Set(filteredCommissionsData.map(comm => comm.saleNumber));
    }
    
    const issues: CommissionValidationIssue[] = [];
    
    // Check each sale
    salesData.forEach(sale => {
      // Skip voided sales
      if (sale.status === 'voided') return;
      
      // If filtering by staff, only check sales that involve this staff member
      if (filteredSaleNumbers && !filteredSaleNumbers.has(sale.saleNumber)) {
        return;
      }
      
      const commissions = commissionsBySaleNumber[sale.saleNumber] || [];
      
      // Check 1: Missing commissions - sale has no commission splits
      if (commissions.length === 0) {
        issues.push({
          saleNumber: sale.saleNumber,
          saleTotal: sale.total,
          commissionTotal: 0,
          commissionRate: 0,
          date: sale.createdAt,
          staffNames: [],
          type: 'missing'
        });
        return; // Skip rate check if no commissions
      }
      
      // Calculate total commission amount
      const commissionTotal = commissions.reduce((sum, comm) => sum + comm.amount, 0);
      
      // Calculate commission rate as percentage of sale total
      const commissionRate = (commissionTotal / sale.total) * 100;
      
      // Check 2: High commission rate - exceeds 5%
      if (commissionRate > 5) {
        issues.push({
          saleNumber: sale.saleNumber,
          saleTotal: sale.total,
          commissionTotal,
          commissionRate,
          date: sale.createdAt,
          staffNames: Array.from(new Set(commissions.map(comm => comm.staffName))),
          type: 'high-rate'
        });
      }
    });
    
    return issues;
  } catch (error) {
    console.error('Error validating commissions:', error);
    throw error;
  }
};

/**
 * Subscribes to real-time updates for sales and commissions validation
 * @param year Year to validate
 * @param month Month to validate (0-11)
 * @param staffId Optional staff ID to filter by
 * @param callback Callback function to handle validation issues
 * @returns Unsubscribe function
 */
export const subscribeToValidationIssues = (
  year: number,
  month: number,
  staffId: string | undefined,
  callback: (issues: SaleValidationIssue[]) => void
): (() => void) => {
  // Set up interval to check for validation issues every 30 seconds
  const intervalId = setInterval(async () => {
    try {
      const issues = await validateSaleSplits(year, month, staffId);
      callback(issues);
    } catch (error) {
      console.error('Error in validation subscription:', error);
    }
  }, 30000); // Check every 30 seconds
  
  // Initial check
  validateSaleSplits(year, month, staffId)
    .then(issues => callback(issues))
    .catch(error => console.error('Error in initial validation check:', error));
  
  // Return unsubscribe function
  return () => {
    clearInterval(intervalId);
  };
};

/**
 * Subscribes to real-time updates for commission validation
 * @param year Year to validate
 * @param month Month to validate (0-11)
 * @param staffId Optional staff ID to filter by
 * @param callback Callback function to handle validation issues
 * @returns Unsubscribe function
 */
export const subscribeToCommissionValidation = (
  year: number,
  month: number,
  staffId: string | undefined,
  callback: (issues: CommissionValidationIssue[]) => void
): (() => void) => {
  // Set up interval to check for validation issues every 30 seconds
  const intervalId = setInterval(async () => {
    try {
      const issues = await validateCommissions(year, month, staffId);
      callback(issues);
    } catch (error) {
      console.error('Error in commission validation subscription:', error);
    }
  }, 30000); // Check every 30 seconds
  
  // Initial check
  validateCommissions(year, month, staffId)
    .then(issues => callback(issues))
    .catch(error => console.error('Error in initial commission validation check:', error));
  
  // Return unsubscribe function
  return () => {
    clearInterval(intervalId);
  };
};
